import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { AppService } from '../app.service';
import { UtilsService } from '../shared/utils.service';

/**
 * System administrator route guard.
 *
 * Blocks a route that exposes platform administration to anyone who is not a system administrator
 * (users.is_sitesoft_admin), and redirects to the dashboard with a message.
 *
 * These pages were previously protected only by hiding their menu entry: the system administrators
 * menu in the top nav is gated on is_sitesoft_admin, but the routes themselves carried nothing
 * beyond AuthGuard, so any authenticated user who knew or guessed a URL could open them. That
 * included the accounts list, the platform user list, all sites, QR codes, the pre-qualification
 * item catalogue and the global configuration screens. Hiding a link is not access control.
 *
 * Usage: add after AuthGuard, which must run first so an unauthenticated visitor is sent to sign-in
 * rather than being told they lack administrator access.
 *
 * That ordering also makes the flag safe to read here. AppService.isAuthenticated is only ever set
 * true by AuthService.updateAccountAuthDetails(), which applies app.user in the same call, so by the
 * time AuthGuard has passed the user is populated and there is no window where a system
 * administrator would be misread as an ordinary user on a hard refresh.
 *
 *   {
 *     path: 'admin',
 *     component: AdminComponent,
 *     canActivate: [AuthGuard, SuperAdminGuard]
 *   }
 *
 * Child routes inherit a parent's canActivate, so guarding a parent covers its children.
 *
 * The API remains the authoritative boundary. Every endpoint behind these pages performs its own
 * check (Controller::isSystemsAdministrator, which also refuses Personal API tokens); this guard
 * stops the page being reachable and keeps the UI honest.
 */
@Injectable({
  providedIn: 'root'
})
export class SuperAdminGuard {

  constructor(
    private app: AppService,
    private utils: UtilsService
  ) {}

  canActivate(next: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean {
    if ( !this.app.user?.is_sitesoft_admin ) {
      this.utils.showToast('This page is restricted to system administrators.');
      this.utils.redirectPage(['/dashboard']);
      return false;
    }

    return true;
  }
}
